import type { CSSProperties } from 'react'
import { isContentSkill, type Skill } from '../types/prisma-enums'
import { formatLevel, formatPercent } from '../lib/format'
// Deep import, not the barrel: `../curriculum` re-exports all five content
// tables, and the parent dashboard has no reason to ship a word list in order
// to draw three segments. `progress` is constants and pure functions only.
import { LEVELS, LEVEL_COUNT } from '../curriculum/progress'

/**
 * Skill progress. design-system.md §3, §8.
 *
 * 🔴 THE RULE THIS COMPONENT EXISTS TO ENFORCE:
 *
 *   Content skills read as a LEVEL      — "Numbers · Level 3 of 3"
 *   Motor skills read as a PERCENTAGE   — "Coordination 81%"
 *
 *   "A percentage where a level is the honest answer is a bug."
 *
 * There is deliberately **no `variant` prop**. The family is derived from the
 * skill itself, using the CONTENT_SKILLS / MOTOR_SKILLS sets generated from the
 * Prisma schema — so a caller cannot render Numeracy as a percentage even by
 * accident. Give it a `variant` and someone eventually will.
 *
 * Why the split is real rather than stylistic: content skills are taught by ONE
 * game and progress through three levels (ADR-037, which supersedes ADR-031's
 * count); motor skills are earned across ALL five games, so a percentage is the
 * honest shape for them and a level is not. A level is also actionable for a
 * parent in a way a percentage never is.
 */

const SKILL_COLOR: Record<Skill, string> = {
  NUMERACY: 'var(--color-skill-numeracy)',
  LITERACY: 'var(--color-skill-literacy)',
  GENERAL_AWARENESS: 'var(--color-skill-general-awareness)',
  SOCIAL_AWARENESS: 'var(--color-skill-social-awareness)',
  MATCHING: 'var(--color-skill-matching)',
  COORDINATION: 'var(--color-skill-coordination)',
  BALANCE: 'var(--color-skill-balance)',
  BODY_AWARENESS: 'var(--color-skill-body-awareness)',
}

export interface SkillProgressProps {
  skill: Skill
  /** Human label — "Numbers", "Coordination". Localised by the caller. */
  label: string
  /** Localised level template, tokens {level} and {of}. */
  levelTemplate?: string
  /**
   * A content skill's level (1–3, ADR-037), or a motor skill's percentage (0–100).
   * Which one it means is decided by the skill, not by the caller.
   */
  value: number
}

export function SkillProgress({ skill, label, value, levelTemplate }: SkillProgressProps) {
  const color = SKILL_COLOR[skill]
  const content = isContentSkill(skill)

  return (
    <div className="flex flex-col gap-1.5 font-dash">
      <div className="flex items-baseline justify-between gap-4">
        <span className="text-dash-xs font-bold text-ink">{label}</span>
        <span className="text-dash-xs font-bold tabular-nums text-ink-soft">
          {content ? formatLevel(value, LEVEL_COUNT, levelTemplate) : formatPercent(value)}
        </span>
      </div>

      {content ? (
        // One segment per level. A level is a discrete step inside the same
        // mechanic, so it is drawn as steps rather than as a continuous fill.
        //
        // 🔴 DRAWN FROM `LEVELS`, NOT FROM A LITERAL — ADR-037. This was
        // `[1, 2, 3, 4, 5]`, which is how a parent came to see five segments
        // with the last two permanently empty for a child who had finished the
        // game. The count and the denominator beside it must be the same fact.
        <div
          className="flex gap-1.5"
          role="img"
          aria-label={`${label}, ${formatLevel(value, LEVEL_COUNT, levelTemplate)}`}
        >
          {LEVELS.map((step) => {
            const reached = step <= value
            return (
              <span
                key={step}
                /*
                 * ⚠️ ONLY A REACHED STEP GROWS IN, AND IT GROWS FROM ITS OWN LEFT
                 * EDGE IN ORDER (`play.css`). An empty segment that animated
                 * would announce a level the child has not got to — and the
                 * stagger is what makes three filled steps read as "one, two,
                 * three" rather than as one wide bar. All of it collapses under
                 * either reduce-motion switch, leaving exactly this static state.
                 */
                className={`h-3 flex-1 rounded-pill${reached ? ' play-stone-on' : ''}`}
                style={
                  {
                    background: reached ? color : 'var(--color-pill-neutral-bg)',
                    '--stone-i': step - 1,
                  } as CSSProperties
                }
              />
            )
          })}
        </div>
      ) : (
        <div
          className="h-3 w-full rounded-pill"
          style={{ background: 'var(--color-pill-neutral-bg)' }}
          role="progressbar"
          aria-valuenow={Math.round(value)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={label}
        >
          {/* ⚠️ THE KEYFRAME GROWS TO THE WIDTH THIS ELEMENT ALREADY HAS, so
              `animation: none` under reduce-motion leaves the correct bar rather
              than an empty one. */}
          <div
            className="play-fill h-3 rounded-pill"
            style={{ width: formatPercent(value), background: color }}
          />
        </div>
      )}
    </div>
  )
}
